import React from "react";
import {
  act,
  fireEvent,
  render,
  screen,
  waitFor,
} from "@testing-library/react";
import "@testing-library/jest-dom";
import FileUploader from "./FileUploader";

const mockAuthFetch = jest.fn();
let mockDropFiles: (files: File[]) => void;

jest.mock("../../../../utils/useAuthFetch", () => ({
  useAuthFetch: () => mockAuthFetch,
}));
jest.mock("../../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({ workspace: { id: "workspace-1" } }),
}));
jest.mock("react-dropzone", () => ({
  useDropzone: ({ onDrop }: { onDrop: (files: File[]) => void }) => {
    mockDropFiles = onDrop;
    return { getRootProps: () => ({}), getInputProps: () => ({}) };
  },
}));

const endpoint = "/api/workspace/meridian/documents/upload/source-document";

function renderUploader() {
  return render(
    <FileUploader
      documentType="Source"
      uploadEndpoint={endpoint}
      metadata={{ documentId: 9, sourceFieldId: 4 }}
    />,
  );
}

function registerId(options: RequestInit | undefined): string {
  return JSON.parse(String(options?.body)).client_upload_id;
}

beforeEach(() => {
  localStorage.clear();
  mockAuthFetch.mockReset();
  jest.spyOn(console, "error").mockImplementation(() => undefined);
  Object.defineProperty(URL, "createObjectURL", {
    configurable: true,
    value: jest.fn(() => "blob:source-test"),
  });
});

afterEach(() => {
  jest.restoreAllMocks();
});

it("starts a new registered attempt when the accepted job fails parsing", async () => {
  const registeredIds: string[] = [];
  const uploadedIds: string[] = [];
  mockAuthFetch.mockImplementation(
    async (_url: string, tag: string, options?: RequestInit) => {
      if (tag === "register source upload") {
        const id = registerId(options);
        registeredIds.push(id);
        return { job_id: id };
      }
      if (tag === "upload file") {
        const id = (options?.body as FormData).get("upload_job_id") as string;
        uploadedIds.push(id);
        return { job_id: id };
      }
      if (tag === "fetch job status") {
        return uploadedIds.length === 1
          ? { status: "failed", error_message: "Failed to parse uploaded file" }
          : {
              status: "completed",
              fileReference: { id: 42, originalName: "source.pdf" },
            };
      }
      throw new Error(`Unexpected request: ${tag}`);
    },
  );

  renderUploader();
  await act(async () => {
    mockDropFiles([
      new File(["source"], "source.pdf", { type: "application/pdf" }),
    ]);
  });
  const retry = await screen.findByTitle("Failed to parse uploaded file");
  fireEvent.click(retry);

  await waitFor(() => expect(uploadedIds).toHaveLength(2));
  expect(registeredIds).toHaveLength(2);
  expect(registeredIds[1]).not.toBe(registeredIds[0]);
  expect(uploadedIds).toEqual(registeredIds);
  const registrationCalls = mockAuthFetch.mock.calls.filter(
    (call) => call[1] === "register source upload",
  );
  const uploadCalls = mockAuthFetch.mock.calls.filter(
    (call) => call[1] === "upload file",
  );
  expect(registrationCalls[0][4]).toBeUndefined();
  expect(uploadCalls[0][4]).toEqual({
    multipartUpload: { stallMs: 30000, responseMs: 60000 },
  });
});

it("offers one-click retry of an interrupted transfer with the same registered job", async () => {
  const registeredIds: string[] = [];
  const uploadedIds: string[] = [];
  mockAuthFetch.mockImplementation(
    async (_url: string, tag: string, options?: RequestInit) => {
      if (tag === "register source upload") {
        const id = registerId(options);
        registeredIds.push(id);
        return { job_id: id };
      }
      if (tag === "upload file") {
        const id = (options?.body as FormData).get("upload_job_id") as string;
        uploadedIds.push(id);
        if (uploadedIds.length === 1) throw new Error("Network interrupted");
        return { job_id: id };
      }
      if (tag === "fetch job status") {
        return {
          status: "completed",
          fileReference: { id: 43, originalName: "source.pdf" },
        };
      }
      throw new Error(`Unexpected request: ${tag}`);
    },
  );

  renderUploader();
  await act(async () => {
    mockDropFiles([
      new File(["source"], "source.pdf", { type: "application/pdf" }),
    ]);
  });
  const retry = await screen.findByRole("button", { name: "Retry upload" });
  expect(
    screen.getByText("Upload interrupted. Retry with this file."),
  ).toBeInTheDocument();
  fireEvent.click(retry);

  await waitFor(() => expect(uploadedIds).toHaveLength(2));
  expect(registeredIds).toHaveLength(1);
  expect(uploadedIds).toEqual([registeredIds[0], registeredIds[0]]);
});

it(// Boundary 3 of #1454: a timed_out job is NONTERMINAL -- retry must
// reconcile the SAME registered job instead of registering a second
// upload. The component poll cadence (POLL_INTERVAL_MS = 5000) and the
// 60-minute tracking deadline (MAX_UPLOAD_JOB_TRACKING_MS) are module
// constants, not injectable. Fake timers drive the poll sleeps and
// Date.now follows the fake clock. The status mock holds its response
// open on a timer the test releases explicitly, so fake-time
// advancement can never consume a poll sleep while a fetch is in flight:
// the loop's post-poll deadline check (component :336) is always the
// first clock read after the sleep, and the deadline crossing lands
// exactly there. pollJobStatus then returns the nonterminal timed_out --
// never a terminal "failed" -- and the retry reconciles the SAME
// registered job.
"reconciles the registered job after a processing timeout instead of registering a second upload", async () => {
  const registeredIds: string[] = [];
  const uploadedIds: string[] = [];
  let polls = 0;
  let phase: "track" | "retry" = "track";
  let releasePoll: (() => void) | null = null;
  let deadlineCrossed = false;

  // Fake timers advance Date.now; the 61-minute crossing applied inside
  // the poll mock (below) lands between the loop's post-poll sleep and
  // its deadline check, because the gate serializes fetches vs timers.
  jest.useFakeTimers();
  try {
    mockAuthFetch.mockImplementation(
      async (_url: string, tag: string, options?: RequestInit) => {
        if (tag === "register source upload") {
          const id = registerId(options);
          registeredIds.push(id);
          return { job_id: id };
        }
        if (tag === "upload file") {
          const id = (options?.body as FormData).get("upload_job_id") as string;
          uploadedIds.push(id);
          return { job_id: id };
        }
        if (tag === "fetch job status" || tag === "fetch restored job status") {
          polls += 1;
          // Hold the response open until the test releases it. While a
          // poll is outstanding, timer advancement cannot fire the next
          // poll sleep, so ordering is fully deterministic.
          await new Promise<void>((resolve) => {
            releasePoll = resolve;
          });
          // Arm the crossing only AFTER poll 1 completes: the loop's next
          // deadline check (its first read after the poll sleep) then
          // sees the closed window and returns timed_out. Arming from
          // the first read would skip poll 1 entirely.
          if (!deadlineCrossed) {
            // Cross the window while this (the first) poll is being
            // answered; the loop's post-poll deadline check -- its first
            // Date.now() read after the sleep -- observes it closed.
            deadlineCrossed = true;
            jest.advanceTimersByTime(61 * 60 * 1000);
          }
          return phase === "retry"
            ? {
                status: "completed",
                fileReference: { id: 44, originalName: "source.pdf" },
              }
            : { status: "processing" };
        }
        throw new Error(`Unexpected request: ${tag}`);
      },
    );

    renderUploader();
    await act(async () => {
      mockDropFiles([
        new File(["source"], "source.pdf", { type: "application/pdf" }),
      ]);
      // Let registration + upload resolve (microtasks under fake timers).
      await Promise.resolve();
    });
    // Poll 1 is now outstanding (gate held). Release it: the loop
    // receives "processing", arms the crossing, and sleeps 5000ms.
    await act(async () => {
      releasePoll?.();
      await Promise.resolve();
    });
    expect(polls).toBe(1);

    // Fire the poll sleep; the loop's deadline check now sees the closed
    // window and pollJobStatus returns timed_out.
    await act(async () => {
      jest.advanceTimersByTime(5001);
      await Promise.resolve();
    });

    // The card shows the danger retry for the timed-out job.
    const retry = screen.getByTitle("Processing timeout");
    // Nonterminal so far: exactly one registration.
    expect(registeredIds).toHaveLength(1);

    // Retry: one-click reconciliation of the SAME registered job. Real
    // timers from here -- RTL's waitFor needs the real clock. Draining
    // pending fake timers first prevents a stale restore-poll timer from
    // firing inside the retry window and masking the outcome (a second
    // poll there would return "completed" for the wrong reason).
    jest.runOnlyPendingTimers();
    jest.useRealTimers();
    phase = "retry";
    fireEvent.click(retry);
    // Release the retry's poll (completes the SAME job).
    await act(async () => {
      releasePoll?.();
      await new Promise((r) => setTimeout(r, 0));
    });

    await waitFor(() => expect(uploadedIds).toHaveLength(2));
    // The regression this pins: timed_out must stay NONTERMINAL. The
    // register endpoint is idempotent per client_upload_id (documents.py
    // :997-1016), and a FAILED job mints a NEW client_upload_id on retry
    // -- so the distinguishing signal is the ID set: timed_out retries
    // the SAME registered job (one distinct ID), while treating it as
    // failed would allocate a second job while a parse may still be live
    // server-side (two distinct IDs).
    expect(uploadedIds).toEqual([registeredIds[0], registeredIds[0]]);
    expect(new Set(registeredIds).size).toBe(1);
  } finally {
    jest.useRealTimers();
  }
});

it("does not offer a fresh upload when the failed job already has a SourceDocument", async () => {
  mockAuthFetch.mockImplementation(
    async (_url: string, tag: string, options?: RequestInit) => {
      if (tag === "register source upload") {
        return { job_id: registerId(options) };
      }
      if (tag === "upload file") {
        return { job_id: (options?.body as FormData).get("upload_job_id") };
      }
      if (tag === "fetch job status") {
        return {
          status: "failed",
          source_document_id: 73,
          error_message: "Indexing or persistence failed",
        };
      }
      throw new Error(`Unexpected request: ${tag}`);
    },
  );

  renderUploader();
  await act(async () => {
    mockDropFiles([
      new File(["source"], "source.pdf", { type: "application/pdf" }),
    ]);
  });
  expect(
    await screen.findByText("Check source document status"),
  ).toBeInTheDocument();
  expect(
    screen.queryByTitle("Indexing or persistence failed"),
  ).not.toBeInTheDocument();
  expect(
    screen.queryByRole("button", { name: "Retry upload" }),
  ).not.toBeInTheDocument();
});
